import {ChangeEvent, DragEvent, FC, useRef, useState} from "react";
import classNames from "classnames/dedupe";
import {cn} from "src/helpers/bem";
import "./styles.scss";
import {Button, CircularProgress, FormHelperText} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  BANNER_IMAGE_ACCEPT,
  BANNER_IMAGE_MAX_BYTES,
} from "src/consts/banners";
import {useUploadBannerImage} from "src/hooks/banners";

const b = cn("image-upload-field");

interface Props {
  className?: string;
  value?: string;
  onChange: (imageUrl: string) => void;
  disabled?: boolean;
}

const MAX_MB = Math.round(BANNER_IMAGE_MAX_BYTES / (1024 * 1024));

// Клиентская валидация дублирует серверные ограничения adminBannerUpload,
// чтобы не тратить запрос на заведомо невалидный файл
const validateFile = (file: File): string | null => {
  if (!BANNER_IMAGE_ACCEPT.includes(file.type)) {
    return "Допустимы только PNG, JPEG или WebP";
  }
  if (file.size > BANNER_IMAGE_MAX_BYTES) {
    return `Файл больше ${MAX_MB} МБ`;
  }
  return null;
};

export const ImageUploadField: FC<Props> = (props) => {
  const {className, value, onChange, disabled = false} = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const uploadMutation = useUploadBannerImage();
  const isBusy = disabled || uploadMutation.isPending;

  const handleFile = (file: File) => {
    const validation = validateFile(file);
    if (validation) {
      setValidationError(validation);
      return;
    }
    setValidationError(null);
    uploadMutation.mutate(file, {
      onSuccess: (data) => {
        if (data.imageUrl) {
          onChange(data.imageUrl);
        }
      },
    });
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
    // сбрасываем input, чтобы повторный выбор того же файла тоже сработал
    e.target.value = "";
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (isBusy) return;
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const openPicker = () => {
    if (!isBusy) {
      inputRef.current?.click();
    }
  };

  const uploadError = uploadMutation.isError
    ? ((uploadMutation.error as any)?.response?.data?.error ??
      "Не удалось загрузить изображение")
    : null;

  const errorText = validationError ?? uploadError;

  return (
    <div className={classNames(b(), className)}>
      <div
        className={b("dropzone", {
          dragover: dragOver,
          disabled: isBusy,
        })}
        onClick={openPicker}
        onDragOver={(e) => {
          e.preventDefault();
          if (!isBusy) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        {uploadMutation.isPending ? (
          <CircularProgress size={32} />
        ) : value ? (
          <img className={b("preview")} src={value} alt="Превью баннера" />
        ) : (
          <>
            <CloudUploadIcon color="action" />
            <span className={b("hint")}>
              Перетащите изображение сюда или нажмите для выбора
            </span>
            <span className={b("hint")}>
              PNG, JPEG или WebP, до {MAX_MB} МБ
            </span>
          </>
        )}

        <input
          ref={inputRef}
          className={b("input")}
          type="file"
          accept={BANNER_IMAGE_ACCEPT.join(",")}
          onChange={handleInputChange}
          disabled={isBusy}
        />
      </div>

      <div className={b("actions")}>
        <Button
          size="small"
          startIcon={<CloudUploadIcon />}
          onClick={openPicker}
          disabled={isBusy}
        >
          {value ? "Заменить" : "Загрузить"}
        </Button>
        <span className={b("hint")}>PNG, JPEG, WebP · до {MAX_MB} МБ</span>
      </div>

      {errorText && <FormHelperText error>{errorText}</FormHelperText>}
    </div>
  );
};
